/**
 * Discount Audit Trail Component
 * Phase 3 Frontend - Display discount action history
 */

import { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Stack,
  Typography,
  Chip,
  Alert,
  Skeleton,
  Box,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
} from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDistanceToNow, format } from 'date-fns';
import { useDiscountAuditTrail } from '../../../hooks/useDiscounts';
import { DiscountAuditAction } from '../../../types/discount.types';

interface DiscountAuditTrailProps {
  invoiceId: string;
  expandedByDefault?: boolean;
}

export default function DiscountAuditTrail({
  invoiceId,
  expandedByDefault = false,
}: DiscountAuditTrailProps) {
  const { data: auditLogs, isLoading, error } = useDiscountAuditTrail(invoiceId);
  const [expandedEntries, setExpandedEntries] = useState<Record<string, boolean>>(
    expandedByDefault && auditLogs?.length
      ? { [auditLogs[0].id]: true }
      : {}
  );

  const handleToggleExpand = (logId: string) => {
    setExpandedEntries((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader title="Discount Audit Trail" />
        <CardContent>
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={60} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader title="Discount Audit Trail" />
        <CardContent>
          <Alert severity="error">
            Failed to load audit trail. Please try again.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!auditLogs || auditLogs.length === 0) {
    return (
      <Card>
        <CardHeader title="Discount Audit Trail" />
        <CardContent>
          <Alert severity="info">
            No discount actions recorded for this invoice.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const getActionColor = (
    action: DiscountAuditAction
  ): 'success' | 'error' | 'warning' | 'info' => {
    switch (action) {
      case DiscountAuditAction.APPROVED:
        return 'success';
      case DiscountAuditAction.REMOVED:
        return 'error';
      case DiscountAuditAction.RECALCULATED:
        return 'warning';
      case DiscountAuditAction.REJECTED:
        return 'error';
      case DiscountAuditAction.CREATED:
        return 'info';
      default:
        return 'info';
    }
  };

  const getTimelineDotColor = (
    action: DiscountAuditAction
  ): 'success' | 'error' | 'warning' | 'info' | 'inherit' => {
    switch (action) {
      case DiscountAuditAction.APPROVED:
        return 'success';
      case DiscountAuditAction.REMOVED:
        return 'error';
      case DiscountAuditAction.RECALCULATED:
        return 'warning';
      case DiscountAuditAction.REJECTED:
        return 'error';
      case DiscountAuditAction.CREATED:
        return 'info';
      default:
        return 'inherit';
    }
  };

  return (
    <Card>
      <CardHeader title="Discount Audit Trail" />
      <CardContent>
        <Timeline position="alternate">
          {auditLogs.map((log, index) => (
            <TimelineItem key={log.id}>
              <TimelineSeparator>
                <TimelineDot color={getTimelineDotColor(log.action)} />
                {index < auditLogs.length - 1 && <TimelineConnector />}
              </TimelineSeparator>

              <TimelineContent sx={{ paddingTop: 1 }}>
                {/* Header Row */}
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Chip
                    label={log.action}
                    size="small"
                    color={getActionColor(log.action)}
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(new Date(log.createdAt), {
                      addSuffix: true,
                    })}
                  </Typography>
                </Stack>

                {/* Discount Details */}
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{log.discountName}</strong>
                </Typography>

                <Typography variant="body2" sx={{ mb: 1 }}>
                  Amount:{' '}
                  <span style={{ color: '#4caf50', fontWeight: 600 }}>
                    ${Number(log.calculatedAmount).toFixed(2)}
                  </span>
                </Typography>

                {/* Performed By */}
                <Typography variant="caption" color="text.secondary" display="block">
                  By {log.PerformedBy.firstName} {log.PerformedBy.lastName}
                </Typography>

                {/* Reason (if removal) */}
                {log.reason && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Reason: {log.reason}
                  </Typography>
                )}

                {/* Expand Button */}
                <Box mt={1}>
                  <IconButton
                    size="small"
                    onClick={() => handleToggleExpand(log.id)}
                    sx={{
                      transform: expandedEntries[log.id]
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 0.3s',
                    }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Expanded Details */}
                <Collapse in={expandedEntries[log.id]}>
                  <Box sx={{ mt: 2, bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Details
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ border: 'none', py: 0.5 }}>
                            <Typography variant="caption" fontWeight={500}>
                              Timestamp
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ border: 'none', py: 0.5 }}>
                            <Typography variant="caption">
                              {format(new Date(log.createdAt), 'PPpp')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ border: 'none', py: 0.5 }}>
                            <Typography variant="caption" fontWeight={500}>
                              Action
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ border: 'none', py: 0.5 }}>
                            <Typography variant="caption">
                              {log.action}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ border: 'none', py: 0.5 }}>
                            <Typography variant="caption" fontWeight={500}>
                              Discount Value
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ border: 'none', py: 0.5 }}>
                            <Typography variant="caption">
                              ${Number(log.discountValue).toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ border: 'none', py: 0.5 }}>
                            <Typography variant="caption" fontWeight={500}>
                              Calculated Amount
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ border: 'none', py: 0.5 }}>
                            <Typography variant="caption">
                              ${Number(log.calculatedAmount).toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {log.reason && (
                          <TableRow>
                            <TableCell sx={{ border: 'none', py: 0.5 }}>
                              <Typography variant="caption" fontWeight={500}>
                                Reason
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ border: 'none', py: 0.5 }}>
                              <Typography variant="caption">
                                {log.reason}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </Collapse>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </CardContent>
    </Card>
  );
}
